import { useCallback, useEffect, useRef, useState } from "react";
import { VariableSizeList } from "react-window";
import PageRenderer from "../components/PageRenderer";
import { usePageHeights } from "../hooks/usePageHeights";

function MeasuredRow({ index, style, setItemHeight, children }) {
  const rowRef = useRef(null);

  useEffect(() => {
    const el = rowRef.current;
    if (!el) return;

    const measure = () => {
      const height = el.getBoundingClientRect().height;
      if (height > 0) setItemHeight(index, height);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [index, setItemHeight]);

  return (
    <div style={style} className="report-preview__row-outer">
      <div ref={rowRef} className="report-preview__row">
        {children}
      </div>
    </div>
  );
}

const VirtualizedReportList = ({
  pageModel,
  chunkCache,
  routeParams,
  onVisibleRange,
  ensureChunkForPage,
}) => {
  const [viewportHeight, setViewportHeight] = useState(
    typeof window !== "undefined" ? window.innerHeight - 120 : 600,
  );
  const { listRef, getItemSize, setItemHeight } = usePageHeights(pageModel);
  const isMobile =
    typeof window !== "undefined" &&
    (window.innerWidth < 768 ||
      /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent));

  useEffect(() => {
    const onResize = () => setViewportHeight(window.innerHeight - 120);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handleItemsRendered = useCallback(
    ({ visibleStartIndex, visibleStopIndex }) => {
      onVisibleRange?.(visibleStartIndex, visibleStopIndex, pageModel);
      for (let i = visibleStartIndex; i <= visibleStopIndex; i++) {
        const page = pageModel[i];
        if (page?.chunkId && typeof page.chunkId === "number") {
          ensureChunkForPage?.(page.chunkId);
        }
      }
    },
    [onVisibleRange, pageModel, ensureChunkForPage],
  );

  const Row = useCallback(
    ({ index, style }) => {
      const page = pageModel[index];
      if (!page) return null;

      return (
        <MeasuredRow index={index} style={style} setItemHeight={setItemHeight}>
          <PageRenderer
            page={page}
            chunkCache={chunkCache}
            routeParams={routeParams}
          />
        </MeasuredRow>
      );
    },
    [pageModel, chunkCache, routeParams, setItemHeight],
  );

  if (!pageModel.length) return null;

  return (
    <VariableSizeList
      ref={listRef}
      height={viewportHeight}
      width="100%"
      itemCount={pageModel.length}
      itemSize={getItemSize}
      estimatedItemSize={1133}
      overscanCount={isMobile ? 1 : 2}
      onItemsRendered={handleItemsRendered}
      className="report-preview__list"
    >
      {Row}
    </VariableSizeList>
  );
};

export default VirtualizedReportList;
